import React, { useCallback, useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import useForm, { FieldValues } from 'hooks/useForm';
import { validateRequiredField, validateZipCode } from 'common/utils/validation.utils';
import InputSelect from 'components/common/InputSelect/InputSelect';
import Button from 'components/common/Button/Button';
import AddressInputs from 'components/common/AddressInputs/AddressInputs';
import { RootStoreContext } from 'index';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { notify } from 'components/common/Toast/toast.helpers';
import { isPuertoRicoZipCode } from 'common/utils/address.utils';

import styles from '../RegistrationProcess.module.scss';
import { AddressInformationFormInput, DefaultYesNoQuestion, PrequalificationStep } from '../registrationProcess.enums';
import { DEFAULT_YES_NO_QUESTION_OPTIONS } from '../registrationProcess.constants';

const AddressInformation = () => {
  const { prequalificationStore } = useContext(RootStoreContext);
  const {
    prequalificationInformation,
    updatePrequalificationInformation,
    intermediaryApplicationId,
    setNextStepByCurrentStep,
  } = prequalificationStore;

  const isThisTheMailingAddress =
    prequalificationInformation?.locationStreet === prequalificationInformation?.mailingStreet &&
    prequalificationInformation?.locationCity === prequalificationInformation?.mailingCity &&
    prequalificationInformation?.locationState === prequalificationInformation?.mailingState &&
    prequalificationInformation?.locationZipCode === prequalificationInformation?.mailingZipCode;

  const [isMailingAddressEqualToCorporate, setIsMailingAddressEqualToCorporate] = useState<boolean | null>(
    !!prequalificationInformation?.mailingStreet ? isThisTheMailingAddress : null,
  );

  const notMailingAddressInitValue = !!prequalificationInformation?.mailingStreet ? DefaultYesNoQuestion.No : null;

  const defaultLocationValues = {
    [AddressInformationFormInput.LocationStreet]: prequalificationInformation?.locationStreet,
    [AddressInformationFormInput.LocationCity]: prequalificationInformation?.locationCity,
    [AddressInformationFormInput.LocationState]: prequalificationInformation?.locationState,
    [AddressInformationFormInput.LocationZipCode]: prequalificationInformation?.locationZipCode,
    [AddressInformationFormInput.IsThisTheMailingAddress]: isThisTheMailingAddress
      ? DefaultYesNoQuestion.Yes
      : notMailingAddressInitValue,
  };

  const defaultMailingValues = {
    [AddressInformationFormInput.MailingStreet]: prequalificationInformation?.mailingStreet,
    [AddressInformationFormInput.MailingCity]: prequalificationInformation?.mailingCity,
    [AddressInformationFormInput.MailingState]: prequalificationInformation?.mailingState,
    [AddressInformationFormInput.MailingZipCode]: prequalificationInformation?.mailingZipCode,
  };

  const { errors, values, setValue, setValues, validateField, handleSubmit, resetForm } = useForm<FieldValues>({
    defaultValues: defaultLocationValues,
    handleValidation: (fieldName, value) => {
      if (isMailingAddressEqualToCorporate) {
        switch (fieldName) {
          case AddressInformationFormInput.LocationStreet: {
            return validateRequiredField(value, fieldName);
          }
          case AddressInformationFormInput.LocationCity: {
            return validateRequiredField(value, fieldName);
          }
          case AddressInformationFormInput.LocationState: {
            return validateRequiredField(value, fieldName);
          }
          case AddressInformationFormInput.LocationZipCode: {
            return validateRequiredField(value, fieldName) || validateZipCode(value, fieldName);
          }
        }
      } else {
        switch (fieldName) {
          case AddressInformationFormInput.MailingStreet: {
            return validateRequiredField(value, fieldName);
          }
          case AddressInformationFormInput.MailingCity: {
            return validateRequiredField(value, fieldName);
          }
          case AddressInformationFormInput.MailingState: {
            return validateRequiredField(value, fieldName);
          }
          case AddressInformationFormInput.MailingZipCode: {
            return validateRequiredField(value, fieldName) || validateZipCode(value, fieldName);
          }
          case AddressInformationFormInput.IsThisTheMailingAddress: {
            return validateRequiredField(value);
          }
        }
      }

      return '';
    },
  });

  const handleChange = useCallback(
    (name: string, value: any): void => {
      setValue(name, value);
    },
    [setValue],
  );

  const handleBlur = useCallback(
    (name: string): void => {
      validateField(name, values[name]);
    },
    [validateField, values],
  );

  useEffect(() => {
    if (isMailingAddressEqualToCorporate) {
      resetForm({ ...defaultLocationValues, ...values });
    } else {
      resetForm({
        ...defaultLocationValues,
        ...defaultMailingValues,
        ...values,
      });
    }
  }, [isMailingAddressEqualToCorporate]);

  const handleSaveInformation = handleSubmit(async () => {
    try {
      if (
        isPuertoRicoZipCode(values[AddressInformationFormInput.LocationZipCode]) ||
        isPuertoRicoZipCode(values[AddressInformationFormInput.MailingZipCode])
      ) {
        notify(ToastMessage.RestrictedZipCode, 'error');

        return;
      }

      await updatePrequalificationInformation(intermediaryApplicationId!, {
        locationStreet: values[AddressInformationFormInput.LocationStreet],
        locationCity: values[AddressInformationFormInput.LocationCity],
        locationState: values[AddressInformationFormInput.LocationState],
        locationZipCode: values[AddressInformationFormInput.LocationZipCode],
        ...(values[AddressInformationFormInput.IsThisTheMailingAddress] === DefaultYesNoQuestion.Yes
          ? {
              mailingStreet: values[AddressInformationFormInput.LocationStreet],
              mailingCity: values[AddressInformationFormInput.LocationCity],
              mailingState: values[AddressInformationFormInput.LocationState],
              mailingZipCode: values[AddressInformationFormInput.LocationZipCode],
            }
          : {
              mailingStreet: values[AddressInformationFormInput.MailingStreet],
              mailingCity: values[AddressInformationFormInput.MailingCity],
              mailingState: values[AddressInformationFormInput.MailingState],
              mailingZipCode: values[AddressInformationFormInput.MailingZipCode],
            }),
        stepType: PrequalificationStep.AddressInformation,
      });

      setNextStepByCurrentStep(PrequalificationStep.AddressInformation);
    } catch (err) {
      notify(ToastMessage.DefaultError, 'error');
    }
  });

  return (
    <div className={styles.infoContainer}>
      <div className={styles.mainHeader}>Business Location Address</div>
      <AddressInputs
        useRequiredSymbol
        values={values}
        errors={errors}
        onChange={handleChange}
        onBlur={handleBlur}
        onInputChange={(value: any) => {
          setValues({
            [AddressInformationFormInput.LocationStreet]: value.streetAddress,
            [AddressInformationFormInput.LocationCity]: value.city,
            [AddressInformationFormInput.LocationState]: value.state,
            [AddressInformationFormInput.LocationZipCode]: value.zip,
          });
        }}
        addressLabels={{
          address: AddressInformationFormInput.LocationStreet,
          city: AddressInformationFormInput.LocationCity,
          state: AddressInformationFormInput.LocationState,
          zip: AddressInformationFormInput.LocationZipCode,
        }}
      />
      <div className={styles.formInput}>
        <InputSelect
          useRequiredSymbol
          items={DEFAULT_YES_NO_QUESTION_OPTIONS}
          currentValue={values[AddressInformationFormInput.IsThisTheMailingAddress]}
          onChange={(value) => {
            setValue(AddressInformationFormInput.IsThisTheMailingAddress, value);
            setIsMailingAddressEqualToCorporate(value === DefaultYesNoQuestion.No ? false : true);
          }}
          label={AddressInformationFormInput.IsThisTheMailingAddress}
          errorMessage={errors[AddressInformationFormInput.IsThisTheMailingAddress]}
        />
      </div>
      {isMailingAddressEqualToCorporate === false && (
        <div className={styles.mailingContainer}>
          <div className={styles.mainHeader}>Business Mailing Address</div>
          <AddressInputs
            useRequiredSymbol
            values={values}
            errors={errors}
            onChange={handleChange}
            onBlur={handleBlur}
            onInputChange={(value: any) => {
              setValues({
                [AddressInformationFormInput.MailingStreet]: value.streetAddress,
                [AddressInformationFormInput.MailingCity]: value.city,
                [AddressInformationFormInput.MailingState]: value.state,
                [AddressInformationFormInput.MailingZipCode]: value.zip,
              });
            }}
            addressLabels={{
              address: AddressInformationFormInput.MailingStreet,
              city: AddressInformationFormInput.MailingCity,
              state: AddressInformationFormInput.MailingState,
              zip: AddressInformationFormInput.MailingZipCode,
            }}
          />
        </div>
      )}
      <div className={styles.formInput}>
        <Button onClick={handleSaveInformation} className={styles.saveButton}>
          Save and Continue
        </Button>
      </div>
    </div>
  );
};

export default observer(AddressInformation);
